import React, { useEffect } from 'react';
import { Shield, AlertTriangle, CheckCircle } from 'lucide-react';

const ExportCompliance = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="dark-page">
      <section className="legal-hero">
        <div className="section-tag">REGULATORY COMPLIANCE</div>
        <h1 className="defense-headline" style={{ fontSize: '56px' }}>Export Compliance</h1>
        <p className="defense-description">ITAR Registered | NATO Certified | EU Compliant</p>
      </section>

      <section className="legal-content">
        <div style={{ maxWidth: '900px', margin: '0 auto', padding: '60px 20px' }}>
          <div className="legal-section">
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
              <Shield size={32} style={{ color: '#D4AF37' }} />
              <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#D4AF37', margin: 0 }}>1. Regulatory Framework</h2>
            </div>
            <p style={{ fontSize: '16px', color: 'rgba(255, 255, 255, 0.85)', lineHeight: '1.8', marginBottom: '30px' }}>
              Labana Dynamics OÜ operates under strict compliance with international defense export regulations:
            </p>
            <ul style={{ fontSize: '16px', color: 'rgba(255, 255, 255, 0.85)', lineHeight: '1.8', marginLeft: '20px', marginBottom: '30px' }}>
              <li><strong>ITAR (International Traffic in Arms Regulations)</strong> - U.S. State Department registration for defense article export</li>
              <li><strong>EAR (Export Administration Regulations)</strong> - U.S. Commerce Department dual-use technology controls</li>
              <li><strong>EU Dual-Use Regulation (2021/821)</strong> - European Union export control framework</li>
              <li><strong>Wassenaar Arrangement</strong> - Multilateral export control regime compliance</li>
            </ul>
          </div>

          <div className="legal-section">
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
              <AlertTriangle size={32} style={{ color: '#D4AF37' }} />
              <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#D4AF37', margin: 0 }}>2. Restricted Destinations</h2>
            </div>
            <p style={{ fontSize: '16px', color: 'rgba(255, 255, 255, 0.85)', lineHeight: '1.8', marginBottom: '20px' }}>
              Wolf Eye systems are <strong>prohibited</strong> from export to:
            </p>
            <ul style={{ fontSize: '16px', color: 'rgba(255, 255, 255, 0.85)', lineHeight: '1.8', marginLeft: '20px', marginBottom: '30px' }}>
              <li>Countries under UN arms embargo</li>
              <li>U.S. State Department arms embargo destinations</li>
              <li>EU Common Position 2008/944/CFSP restricted nations</li>
              <li>OFAC (Office of Foreign Assets Control) sanctioned entities</li>
              <li>Terrorist organizations or non-state actors</li>
            </ul>
          </div>

          <div className="legal-section">
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
              <CheckCircle size={32} style={{ color: '#D4AF37' }} />
              <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#D4AF37', margin: 0 }}>3. End-User Requirements</h2>
            </div>
            <p style={{ fontSize: '16px', color: 'rgba(255, 255, 255, 0.85)', lineHeight: '1.8', marginBottom: '20px' }}>
              All purchases require:
            </p>
            <ul style={{ fontSize: '16px', color: 'rgba(255, 255, 255, 0.85)', lineHeight: '1.8', marginLeft: '20px', marginBottom: '30px' }}>
              <li><strong>End-User Certificate (EUC)</strong> - Government-issued certificate stating final destination and authorized use</li>
              <li><strong>Import License</strong> - Valid import authorization from destination country</li>
              <li><strong>Non-Diversion Statement</strong> - Certification that systems will not be re-exported without approval</li>
              <li><strong>Security Clearance</strong> - Proof of government or defense organization affiliation</li>
            </ul>
          </div>

          <div className="legal-section">
            <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#D4AF37', marginBottom: '20px' }}>4. Technology Transfer Controls</h2>
            <p style={{ fontSize: '16px', color: 'rgba(255, 255, 255, 0.85)', lineHeight: '1.8', marginBottom: '30px' }}>
              Technical data, blueprints, and source code are classified as defense articles. Sharing, reverse engineering, or unauthorized access is prohibited under:
            </p>
            <ul style={{ fontSize: '16px', color: 'rgba(255, 255, 255, 0.85)', lineHeight: '1.8', marginLeft: '20px', marginBottom: '30px' }}>
              <li>ITAR § 120.10 (Technical Data)</li>
              <li>EAR § 734.7 (Technology and Software)</li>
              <li>EU Dual-Use Regulation Annex I (Category 5 & 6)</li>
            </ul>
          </div>

          <div className="legal-section">
            <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#D4AF37', marginBottom: '20px' }}>5. Audit and Compliance</h2>
            <p style={{ fontSize: '16px', color: 'rgba(255, 255, 255, 0.85)', lineHeight: '1.8', marginBottom: '30px' }}>
              Labana Dynamics maintains:
            </p>
            <ul style={{ fontSize: '16px', color: 'rgba(255, 255, 255, 0.85)', lineHeight: '1.8', marginLeft: '20px', marginBottom: '30px' }}>
              <li>Annual ITAR compliance audits</li>
              <li>Export transaction records for 7 years</li>
              <li>Internal Compliance Program (ICP) with designated compliance officer</li>
              <li>Regular staff training on export regulations</li>
            </ul>
          </div>

          <div className="legal-section">
            <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#D4AF37', marginBottom: '20px' }}>6. Violations and Penalties</h2>
            <p style={{ fontSize: '16px', color: 'rgba(255, 255, 255, 0.85)', lineHeight: '1.8', marginBottom: '30px' }}>
              Export violations may result in:
            </p>
            <ul style={{ fontSize: '16px', color: 'rgba(255, 255, 255, 0.85)', lineHeight: '1.8', marginLeft: '20px', marginBottom: '30px' }}>
              <li>Criminal prosecution (up to 20 years imprisonment)</li>
              <li>Civil penalties (up to $1 million per violation - ITAR)</li>
              <li>Debarment from defense contracts</li>
              <li>Asset seizure and forfeiture</li>
            </ul>
          </div>

          <div className="legal-section">
            <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#D4AF37', marginBottom: '20px' }}>7. Reporting Requirements</h2>
            <p style={{ fontSize: '16px', color: 'rgba(255, 255, 255, 0.85)', lineHeight: '1.8', marginBottom: '20px' }}>
              Clients must immediately report:
            </p>
            <ul style={{ fontSize: '16px', color: 'rgba(255, 255, 255, 0.85)', lineHeight: '1.8', marginLeft: '20px', marginBottom: '30px' }}>
              <li>Unauthorized access attempts or data breaches</li>
              <li>Lost or stolen Wolf Eye modules</li>
              <li>Changes in end-use or end-user</li>
              <li>Requests for technical data from third parties</li>
            </ul>
          </div>

          <div className="legal-section">
            <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#D4AF37', marginBottom: '20px' }}>8. Compliance Contact</h2>
            <p style={{ fontSize: '16px', color: 'rgba(255, 255, 255, 0.85)', lineHeight: '1.8', marginBottom: '10px' }}>
              For export license inquiries or compliance questions:
            </p>
            <p style={{ fontSize: '16px', color: '#D4AF37', lineHeight: '1.8' }}>
              Email: info@labanadynamics.com<br/>
              Subject: Export Compliance - [Your Organization]<br/>
              Response Time: 48 hours
            </p>
          </div>

          <div style={{ marginTop: '60px', padding: '30px', background: 'rgba(212, 175, 55, 0.1)', border: '1px solid rgba(212, 175, 55, 0.3)' }}>
            <p style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.7)', lineHeight: '1.8', margin: 0 }}>
              <strong style={{ color: '#D4AF37' }}>Disclaimer:</strong> This page provides general guidance only. Specific export requirements vary by destination and end-use. Consult legal counsel and obtain proper export licenses before initiating any transaction.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ExportCompliance;